/**
 * Görsel Kütüphanesi — the reference's `data-screen="assets"` (`Screens.assets`, `m08c-library.js`).
 *
 * Four cards in the reference's own order: the storage status line, the upload dropzone, the
 * library grid with its search box, the font asset table, and the project transfer block. Every
 * control the reference offers is here and works — searching filters the grid, «Hash kopyala»
 * copies the full digest, «Şablona uygula…» writes a template DRAFT and opens the editor,
 * «Font yükle» goes through the same metadata gate the editor uses, and the project can be
 * exported, dry-run imported and cleared.
 *
 * Two things this surface does differently from the prototype, both deliberate:
 *
 *   - the storage box states where the library it is showing actually came from. The reference's
 *     own sentence («bu tarayıcıda IndexedDB'de saklanıyor») is true of the reference's world and of
 *     the demo that reproduces it; in the product the library is a server's, read over the network,
 *     and nothing about it is in this browser's IndexedDB. The read says which — `origin` — because
 *     a screen must not guess where somebody's files are;
 *   - an uploaded SVG is refused rather than sanitised. The reference strips with an
 *     element/attribute allowlist; a half-ported allowlist that lets something through would be
 *     worse than a refusal, and sanitisation is a server-side control in the product.
 */
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useV07Commands } from '../data/commands-context';
import type { AssetUsageSummary, PreviewFailure } from '../data/queries';
import { useV07Queries } from '../data/queries-context';
import { useDemoData, useDemoReset } from '../data/store';
import type { DemoTemplate } from '../data/types';
import { type ReadResult, useRead } from '../data/use-read';
import {
  ASSET_LIMITS,
  alphaNote,
  applyAssetToDraft,
  applyImportedSettings,
  copyText,
  downloadText,
  exportProject,
  type FontMeta,
  guessFontMeta,
  type ImportPlan,
  type LibraryAsset,
  PROJECT_SCHEMA_NAME,
  PROJECT_SCHEMA_VERSION,
  planProjectImport,
} from '../lib/asset-library';
import { PROTOTYPE_VERSION, todayIso, trLower } from '../lib/reference';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';

interface Line {
  key: string;
  node: ReactNode;
}

const EMPTY_PREVIEWS: Record<string, PreviewFailure> = {};

/**
 * Why a listed row has no picture — three answers, because they are three different facts.
 *
 * «Yetki yok» sends an administrator to whoever manages roles; «bulunamadı» sends them to whoever
 * manages storage; «okunamadı» says to try again. One sentence for all three sent everybody to the
 * wrong place, and the third was not even reachable: a fault used to fail the whole library instead.
 */
const PREVIEW_REASON_TR: Record<PreviewFailure, string> = {
  forbidden: 'önizleme yetkisi yok',
  missing: 'görsel içeriği bulunamadı',
  unreadable: 'önizleme okunamadı',
};

/**
 * «Nerede kullanılıyor» — one line per picture, with the three answers kept apart.
 *
 * The distinction this line exists to keep is between «nothing uses this» and «nobody answered».
 * They used to look the same, because the count came from a bare filter over whatever the browser
 * held and a failure could only ever produce zero. That is the most dangerous confusion this screen
 * can make: zero is the number somebody deletes on.
 *
 * So a read that is still running says so, a read that failed says so and offers a retry, and only
 * a real answer prints a number. The number is DISTINCT TEMPLATES — a template counts once however
 * many layers draw the picture and however many of its releases carry it — and the manifest count
 * beside it is a count of drawn documents, never of recipients.
 */
function AssetUsageLine({
  assetId,
  read,
  onRetry,
}: {
  assetId: string;
  read: ReadResult<Record<string, AssetUsageSummary>>;
  onRetry: () => void;
}): ReactElement {
  if (read.status === 'loading')
    return (
      <span className="small muted" data-usage={assetId} data-usage-state="loading">
        kullanım okunuyor…
      </span>
    );

  const found = read.value?.[assetId];
  /* Either the whole read failed, or this one picture's own request did and was left out of the map. */
  if (found === undefined)
    return (
      <span className="small muted" data-usage={assetId} data-usage-state="error">
        {'kullanım okunamadı '}
        <button className="btn btn-ghost btn-sm" type="button" data-usage-retry={assetId} onClick={onRetry}>
          Yeniden dene
        </button>
      </span>
    );

  const parts: string[] = [
    found.templates ? `${found.templates} şablonda kullanılıyor` : 'hiçbir şablonda kullanılmıyor',
  ];
  if (found.manifests) parts.push(`${found.manifests} render manifestinde kayıtlı`);
  return (
    <span
      className="small muted"
      data-usage={assetId}
      data-usage-state={read.stale ? 'stale' : 'ready'}
      title="Şablon sayısı benzersizdir: bir şablonun taslağı ve yayınları birlikte tek şablon sayılır. Render manifesti çizilmiş belge sayısıdır, alıcı sayısı değildir."
    >
      {parts.join(' · ')}
      {read.stale ? ' · güncel değil' : ''}
    </span>
  );
}

const Spinner = ({ label }: { label: string }): ReactElement => (
  <div className="muted" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
    <span className="spinner" />
    {` ${label}`}
  </div>
);

/** `renderImportPlan` — the dry-run verdict plus up to twenty lines of each severity. */
function ImportPlanView({ plan }: { plan: ImportPlan }): ReactElement {
  const list = (title: string, items: string[], cls: string): ReactElement | null =>
    items.length ? (
      <div className={cls} style={{ marginTop: '8px' }} key={title}>
        <b>{title}</b>
        <ul style={{ margin: '6px 0 0 18px' }}>
          {items.slice(0, 20).map((x) => (
            <li key={x}>{x}</li>
          ))}
          {items.length > 20 ? <li key="rest">{`… ${items.length - 20} tane daha`}</li> : null}
        </ul>
      </div>
    ) : null;
  const c = plan.counts;
  return (
    <>
      {plan.ok ? (
        <div className="ok-box">
          <b>Dry-run geçti.</b>
          {` Yazılacak: ${c.assets ?? 0} görsel, ${c.fontAssets ?? 0} font, ${c.templates ?? 0} şablon, ${c.releases ?? 0} release, ${c.credentials ?? 0} kayıt, ${c.batches ?? 0} yükleme.`}
        </div>
      ) : (
        <div className="warn-box">
          <b>İçe aktarım reddedildi.</b>
          {` ${plan.errors.length} sorun bulundu; hiçbir kayıt yazılmadı.`}
        </div>
      )}
      {list('Hatalar', plan.errors, 'warn-box')}
      {list('Uyarılar', plan.warnings, 'info-box')}
      {list('Notlar', plan.notes, 'help')}
    </>
  );
}

/**
 * The single font metadata gate — `openFontMetadataModal`.
 *
 * The library and the editor use the same dialog on purpose: a wrong family name or weight breaks
 * the release's font digest contract, so the values are guessed from the file name and then
 * confirmed, and the licence checkbox is a hard gate rather than a formality.
 */
function FontMetaModal({
  file,
  onCancel,
  onSave,
}: {
  file: File;
  onCancel: () => void;
  onSave: (meta: FontMeta) => void;
}): ReactElement {
  const guess = useMemo(() => guessFontMeta(file.name), [file]);
  const [family, setFamily] = useState(guess.family);
  const [weight, setWeight] = useState(String(guess.weight));
  const [style, setStyle] = useState(guess.italic ? 'italic' : 'normal');
  const [licensed, setLicensed] = useState(false);
  const [error, setError] = useState('');

  const save = (): void => {
    if (!family.trim()) {
      setError('Font family adı boş olamaz.');
      return;
    }
    if (!licensed) {
      setError('Lisans onayı olmadan font kaydedilemez.');
      return;
    }
    onSave({ family: family.trim(), weight: Number.parseInt(weight, 10), style, licenseAcknowledged: true });
  };

  return (
    <Modal label="Font meta verisi" onClose={onCancel}>
      <ModalHead title="Font meta verisi" onClose={onCancel} />
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="file-chip">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>{`${file.name} · ${Math.round((file.size || 0) / 1024)} KB`}</span>
        </div>
        <div className="info-box small">
          {'Dosya adından tahmin edilen değerler ön dolduruldu. '}
          <b>Yanlış aile adı veya kalınlık, release'in font digest sözleşmesini bozar</b>
          {'; lütfen doğrulayın.'}
        </div>
        <div className="field">
          <label htmlFor="fmFamily">Font family adı</label>
          <input
            className="input"
            id="fmFamily"
            value={family}
            required
            onChange={(e) => setFamily(e.target.value)}
          />
        </div>
        <div className="field-row">
          <div className="field">
            <label htmlFor="fmWeight">Gerçek kalınlık</label>
            <select
              className="select"
              id="fmWeight"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
            >
              {[100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => (
                <option value={String(w)} key={w}>
                  {String(w)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="fmStyle">Stil</label>
            <select className="select" id="fmStyle" value={style} onChange={(e) => setStyle(e.target.value)}>
              <option value="normal">normal</option>
              <option value="italic">italic</option>
            </select>
          </div>
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="fmLicense"
            checked={licensed}
            onChange={(e) => setLicensed(e.target.checked)}
          />
          {' Bu fontun kullanım ve gömme (embedding) lisansına sahip olduğumu onaylıyorum.'}
        </label>
        <div id="fmErr" className="warn-box" hidden={!error}>
          {error}
        </div>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onCancel}>
          Vazgeç
        </button>
        <button className="btn btn-primary" id="fmGo" type="button" onClick={save}>
          Fontu kaydet
        </button>
      </div>
    </Modal>
  );
}

/** «Görseli şablona uygula» — a draft write, never a release (`m08c-library.js` §13). */
function ApplyAssetModal({
  asset,
  templates,
  onCancel,
  onApply,
}: {
  asset: LibraryAsset;
  templates: DemoTemplate[];
  onCancel: () => void;
  onApply: (templateId: string, resizeCanvas: boolean) => void;
}): ReactElement {
  const list = useMemo(
    () => templates.slice().sort((x, y) => x.name.localeCompare(y.name, 'tr')),
    [templates],
  );
  const [templateId, setTemplateId] = useState(list[0]?.id ?? '');
  const [resize, setResize] = useState(true);

  return (
    <Modal label="Görseli şablona uygula" onClose={onCancel}>
      <ModalHead title="Görseli şablona uygula" onClose={onCancel} />
      <div className="card-pad stack" style={{ gap: '12px' }}>
        <div className="info-box">
          {'Bu işlem '}
          <b>yayın yapmaz</b>
          {
            '. Seçilen şablonun taslağına yeni arka plan yazılır ve editör açılır; release yalnız editördeki yayın kapısından çıkar.'
          }
        </div>
        <div className="field">
          <label htmlFor="aaTpl">Şablon</label>
          <select
            className="select"
            id="aaTpl"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
          >
            {list.map((t) => {
              /* The reference labels with the LAST release, not with `currentReleaseId`. */
              const last = t.releases[t.releases.length - 1];
              return (
                <option value={t.id} key={t.id}>
                  {`${t.name}${last ? ` · yayında v${last.releaseNumber}` : ' · yayınlanmamış'}`}
                </option>
              );
            })}
          </select>
        </div>
        <label className="check">
          <input
            type="checkbox"
            id="aaCanvas"
            checked={resize}
            onChange={(e) => setResize(e.target.checked)}
          />
          {` Canvas'ı görselin native ölçüsüne (${asset.width}×${asset.height}) getir`}
        </label>
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" data-close="1" type="button" onClick={onCancel}>
          Vazgeç
        </button>
        <button
          className="btn btn-primary"
          id="aaGo"
          type="button"
          onClick={() => onApply(templateId, resize)}
        >
          Taslağa uygula ve editörü aç
        </button>
      </div>
    </Modal>
  );
}

export function AssetsScreen(): ReactElement {
  const db = useDemoData();
  const commands = useV07Commands();
  const resetDemo = useDemoReset();
  const queries = useV07Queries();
  const navigate = useNavigate();
  const toast = useToast();

  /*
   * The library is READ, not held.
   *
   * It used to be two component states seeded from the reference's own dump, and `ingest*` kept what
   * it made in them — so an upload lived until the tab was refreshed, the editor's picker never saw
   * it, and a picture applied to a template carried an id no server had. Both surfaces now ask the
   * same read: the demo answers from its own library, the product from `GET /assets` and `GET /fonts`
   * with the bytes it is allowed to read.
   */
  const library = useRead('asset-library', () => queries.assetLibrary());
  const assets = library.value?.assets ?? [];
  const fonts = library.value?.fonts ?? [];
  /** Listed, but the bytes did not arrive — and WHY not, per row. */
  const previews = library.value?.previews ?? EMPTY_PREVIEWS;
  const [query, setQuery] = useState('');
  const [over, setOver] = useState(false);
  const [reading, setReading] = useState(false);
  const [uploadLines, setUploadLines] = useState<Line[]>([]);
  const [fontLines, setFontLines] = useState<Line[]>([]);
  const [fontQueue, setFontQueue] = useState<File[]>([]);
  const [applyTo, setApplyTo] = useState<LibraryAsset | null>(null);
  const [checking, setChecking] = useState(false);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [confirmImport, setConfirmImport] = useState(false);
  const [importResult, setImportResult] = useState<Line | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const shown = useMemo(() => {
    const q = trLower(query.trim());
    return q ? assets.filter((a) => trLower(a.originalFileName || '').includes(q)) : assets;
  }, [assets, query]);

  /*
   * Where each picture is really used, asked of the server rather than counted on screen.
   *
   * The tile used to count `db.templates` — the templates this browser happened to be holding. The
   * register is paged, so a template nobody had scrolled to did not exist as far as the tile was
   * concerned, and a picture in daily use could read «kullanılmıyor» beside its own thumbnail.
   *
   * Keyed by the WHOLE library rather than by what the search box is showing, so typing filters the
   * tiles without re-asking the server about any of them; `useRead` keeps the answer until the
   * question changes or a command changed the world, which is what refreshes it after «Şablona
   * uygula», a removal or a publish.
   */
  const usageKey = useMemo(() => assets.map((a) => a.id).join(','), [assets]);
  const usage = useRead(`asset-usage:${usageKey}`, () =>
    usageKey === '' ? {} : queries.assetUsage(usageKey.split(',')),
  );

  /*
   * The upload command does the uploading; this walks the files and reports what came back.
   *
   * Success is the command's, which means the server's: the product posts the bytes, and the id, the
   * digest and the dimensions in the line below are the ones the server stored AFTER sanitising an SVG
   * or stripping a raster's chunks. A refusal is reported as a refusal — and because the command only
   * invalidates the reads when it succeeded, a refused file leaves the library exactly as it was.
   */
  async function ingestImages(files: FileList | null): Promise<void> {
    const list = Array.from(files ?? []);
    if (!list.length) return;
    setReading(true);
    setUploadLines([]);
    const lines: Line[] = [];
    for (const file of list) {
      const r = await commands.uploadAsset({ file });
      const a = r.asset;
      if (!r.ok || !a) {
        lines.push({
          key: `err-${file.name}-${lines.length}`,
          node: (
            <div className="warn-box">
              <b>{file.name}</b>
              {` kabul edilmedi: ${r.message ?? 'yükleme reddedildi'}`}
            </div>
          ),
        });
        continue;
      }
      lines.push({
        key: `${a.id}-${lines.length}`,
        node: (
          <div className="ok-box">
            <b>{a.originalFileName}</b>
            {` ${r.deduped ? 'zaten kütüphanede (aynı içerik)' : 'eklendi'} · ${a.width}×${a.height} · SHA-256 `}
            <span className="mono">{`${a.sha256.slice(0, 24)}…`}</span>
            {alphaNote(a)}
          </div>
        ),
      });
    }
    setReading(false);
    setUploadLines(lines);
  }

  /* The reference walks the selected fonts one at a time, asking for metadata before each; the
     queue here is that same sequence, one dialog at a time. */
  async function resolveFontMeta(meta: FontMeta | null): Promise<void> {
    const [file, ...rest] = fontQueue;
    if (!file) return;
    if (!meta) {
      setFontLines((prev) => [
        ...prev,
        {
          key: `skip-${file.name}-${prev.length}`,
          node: (
            <div className="info-box">
              <b>{file.name}</b>
              {' atlandı (kullanıcı vazgeçti).'}
            </div>
          ),
        },
      ]);
    } else {
      /*
       * Family, weight, style and the licence acknowledgement go WITH the bytes: the server refuses a
       * face whose licence was not acknowledged and stores the face under exactly this metadata, so
       * the same four values reach it from here and from the editor's own dialog.
       */
      const r = await commands.uploadFont({ file, meta });
      const f = r.font;
      if (r.ok && f) {
        setFontLines((prev) => [
          ...prev,
          {
            key: `${f.id}-${prev.length}`,
            node: (
              <div className="ok-box">
                <b>{`${f.family} ${f.weight} ${f.style}`}</b>
                {` ${r.deduped ? 'zaten kayıtlı' : 'eklendi'} · `}
                <span className="mono">{`${f.sha256.slice(0, 24)}…`}</span>
                {f.state === 'loaded' ? null : ' · yüzü bu oturumda yüklenemedi'}
              </div>
            ),
          },
        ]);
      } else {
        setFontLines((prev) => [
          ...prev,
          {
            key: `fonterr-${file.name}-${prev.length}`,
            node: (
              <div className="warn-box">
                <b>{file.name}</b>
                {` kabul edilmedi: ${r.message ?? 'yükleme reddedildi'}`}
              </div>
            ),
          },
        ]);
      }
    }
    setFontQueue(rest);
  }

  /*
   * The picture goes into the template's DRAFT, never into a published release (rules 3 and 4): the
   * layer is composed here, and the command writes the whole draft — one write, one audit entry,
   * and the publish gate still the only way it can reach a credential.
   */
  async function applyToTemplate(templateId: string, resizeCanvas: boolean): Promise<void> {
    const asset = applyTo;
    setApplyTo(null);
    if (!asset) return;
    /*
     * Composed, saved, and only then written locally.
     *
     * The draft was composed INTO the store first and the command read it back out — but the store is
     * React state, so the read answered with the draft as it was before the composition: the save
     * carried the old drawing and the screen said «uygulandı» about a picture the server never got.
     * The composition travels with the call now, and nothing is written here until the server has
     * taken it.
     */
    const current = db.templates.find((x) => x.id === templateId);
    if (!current) {
      toast('Şablon bulunamadı.');
      return;
    }
    const at = new Date().toISOString();
    const composed = structuredClone(current);
    applyAssetToDraft(composed, asset, resizeCanvas, at);
    const r = await commands.applyAssetToTemplate({
      templateId,
      assetId: asset.id,
      resizeCanvas,
      draft: composed.draft,
    });
    if (!r.ok) {
      toast(r.message ?? 'Taslağa uygulanamadı');
      return;
    }
    commands.patchLocal((draft) => {
      const t = draft.templates.find((x) => x.id === templateId);
      if (!t) return;
      t.draft = composed.draft;
      t.userModified = true;
      t.updatedAt = at;
    });
    toast('Taslağa uygulandı — yayın için editördeki kapıyı kullanın');
    navigate(`/admin/templates/${templateId}/editor`);
  }

  /* API GAP: project.import — the product's import command has no route yet (PO 7.5), and its
     graph-integrity validation (rule 9) is server-side; the dry run here is the subset a browser
     can honestly perform. */
  async function readImportFile(file: File | undefined): Promise<void> {
    if (!file) return;
    setImportResult(null);
    setPlan(null);
    setChecking(true);
    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch (e) {
      setChecking(false);
      setImportResult({
        key: 'parse',
        node: <div className="warn-box">{`Dosya JSON olarak okunamadı: ${(e as Error).message}`}</div>,
      });
      return;
    }
    const next = await planProjectImport(parsed, file.size, { assets, fonts });
    setChecking(false);
    setPlan(next);
    if (next.ok) setConfirmImport(true);
  }

  /**
   * The commit. The reference writes memory and IndexedDB in one journalled transaction and rolls
   * the journal back on any failure; here the provider's update is the transaction — the whole
   * mutation is applied to a copy and swapped in, so a throw leaves the session untouched.
   */
  function commitImport(): void {
    const current = plan;
    setConfirmImport(false);
    if (!current?.ok) return;
    commands.patchLocal((draft) => {
      /*
       * The imported library goes where this session's additions go.
       *
       * Project import is the DEMO's own capability — the product has no route for it (PO 7.5) — so
       * what it adds is the session library the demo's read returns beside the reference's own.
       * Imported faces carry bytes nothing registered with `FontFace`, so they are not `loaded`.
       */
      draft.sessionAssets = [...(draft.sessionAssets ?? []), ...current.assets];
      draft.sessionFonts = [
        ...(draft.sessionFonts ?? []),
        ...current.fontAssets.map((f) => ({ ...f, state: 'pending' as const })),
      ];
    });
    commands.patchLocal((draft) => {
      for (const raw of current.templates) {
        const t = raw as unknown as DemoTemplate;
        const i = draft.templates.findIndex((x) => x.id === t.id);
        if (i >= 0) draft.templates[i] = { ...(draft.templates[i] as DemoTemplate), ...t };
        else draft.templates.push(t);
      }
      for (const d of current.drafts) {
        const t = draft.templates.find((x) => x.id === d.templateId);
        if (t) t.draft = d.draft as unknown as DemoTemplate['draft'];
      }
      for (const raw of current.releases) {
        const t = draft.templates.find((x) => x.id === raw.templateId);
        if (!t) continue;
        const release = raw as unknown as DemoTemplate['releases'][number];
        if (!t.releases.some((r) => r.id === release.id)) t.releases.push(release);
      }
      for (const raw of current.subjects) {
        const s = raw as unknown as (typeof draft.subjects)[number];
        if (!draft.subjects.some((x) => x.id === s.id)) draft.subjects.push(s);
      }
      for (const raw of current.credentials) {
        const c = raw as unknown as (typeof draft.credentials)[number];
        if (!draft.credentials.some((x) => x.id === c.id)) draft.credentials.push(c);
      }
      for (const raw of current.batches) {
        const b = raw as unknown as (typeof draft.batches)[number];
        if (!draft.batches.some((x) => x.id === b.id)) draft.batches.push(b);
      }
      if (current.settings) {
        applyImportedSettings(draft.settings as unknown as Record<string, unknown>, current.settings);
      }
    });
    const c = current.counts;
    toast('Proje içe aktarıldı');
    setPlan(null);
    setImportResult({
      key: 'done',
      node: (
        <div className="ok-box">
          {`İçe aktarıldı: ${c.assets ?? 0} görsel, ${c.fontAssets ?? 0} font, ${c.templates ?? 0} şablon, ${c.credentials ?? 0} kayıt, ${c.batches ?? 0} yükleme.`}
        </div>
      ),
    });
  }

  function clearUserData(): void {
    setConfirmClear(false);
    /* The reference clears IndexedDB and reloads; the demo has nothing on disk to clear, so the
       equivalent is putting the session back to the prototype's own seed. */
    resetDemo();
    /* Back to the prototype's own seed: the session's own additions go with it. */
    commands.patchLocal((draft) => {
      draft.sessionAssets = [];
      draft.sessionFonts = [];
    });
    setQuery('');
    setUploadLines([]);
    setFontLines([]);
    setImportResult(null);
    setPlan(null);
    toast('Kullanıcı verisi sıfırlandı');
  }

  return (
    <section data-screen="assets">
      <div className="page-head">
        <div>
          <span className="eyebrow">Tasarım dosyaları</span>
          <h1>Görsel Kütüphanesi</h1>
          <p className="lead">
            Şablonların arka planı ve görsel katmanları buradan gelir. Yüklenen her dosyanın ölçüsü, türü,
            şeffaflığı ve SHA-256 değeri yükleme anında hesaplanır; önceden tanımlı bir hash listesi gerekmez.
          </p>
        </div>
      </div>
      <div id="assetsBody">
        <div className="ok-box" style={{ fontSize: '14px', marginBottom: '16px' }}>
          <b>Depolama:</b>
          {library.value?.origin === 'browser'
            ? " Görseller, fontlar ve çalışma alanı bu tarayıcıda IndexedDB'de saklanıyor; sayfa yenilenince korunur."
            : ' Görsel ve font kütüphanesi sunucudan okunur.'}
        </div>

        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-head">
            <h2>Görsel yükle</h2>
            <span className="small muted">
              {`PNG · JPG · SVG · en çok ${Math.round(ASSET_LIMITS.maxBytes / 1048576)} MB · ${ASSET_LIMITS.minPx}–${ASSET_LIMITS.maxPx} px`}
            </span>
          </div>
          <div className="card-pad stack" style={{ gap: '10px' }}>
            <label
              className={`dropzone${over ? ' is-over' : ''}`}
              id="alDz"
              htmlFor="alFile"
              onDragEnter={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                void ingestImages(e.dataTransfer.files);
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
                <path d="M12 16V5" />
                <path d="M7 10l5-5 5 5" />
                <path d="M4 17v2a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-2" />
              </svg>
              <b>Dosyaları buraya bırakın ya da seçin</b>
              <span className="small">
                Genişlik, yükseklik, tür, boyut, SHA-256 ve şeffaflık otomatik algılanır · SVG içeriği
                temizlenir
              </span>
            </label>
            <input
              type="file"
              id="alFile"
              accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
              multiple
              hidden
              onChange={(e) => {
                void ingestImages(e.target.files);
                e.target.value = '';
              }}
            />
            <div id="alResult">
              {reading ? (
                <Spinner label="Okunuyor, hash hesaplanıyor…" />
              ) : (
                uploadLines.map((l) => <div key={l.key}>{l.node}</div>)
              )}
            </div>
            <div className="help">
              {'Asset içerik-adreslidir: kimlik '}
              <code>{'asset_<sha256[0:24]>'}</code>
              {
                " olarak digest'ten türetilir. Aynı içerik ikinci kez yüklenirse yeni kayıt açılmaz, mevcut immutable asset döner. Var olan bir asset ID'sinin içeriği hiçbir yoldan değiştirilemez."
              }
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-head">
            <h2>
              {'Kütüphane '}
              <span className="small muted">{`(${assets.length} görsel)`}</span>
            </h2>
            <input
              className="input search"
              id="alSearch"
              placeholder="Dosya adı ara…"
              style={{ maxWidth: '260px' }}
              aria-label="Görsel ara"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="card-pad">
            <div className="asset-grid" id="alGrid">
              {/*
               * Four answers, not one «boş».
               *
               * «Still reading», «the read failed, try again», «the library is empty» and «nothing
               * matches what you typed» are four different facts, and an empty grid for all of them
               * tells an administrator their images are gone when the server merely did not answer.
               */}
              {library.status === 'loading' && library.value === null ? (
                <Spinner label="Kütüphane okunuyor…" />
              ) : library.status === 'error' ? (
                <div className="warn-box" role="alert" id="alReadError">
                  <b>Kütüphane okunamadı.</b>
                  {` ${library.error?.message ?? 'Sunucuya ulaşılamadı'} `}
                  {library.error?.kind === 'session' ? null : (
                    <button
                      className="btn btn-secondary btn-sm"
                      type="button"
                      id="alRetry"
                      onClick={library.retry}
                    >
                      Yeniden dene
                    </button>
                  )}
                </div>
              ) : assets.length === 0 ? (
                <p className="muted">Kütüphanede görsel yok.</p>
              ) : shown.length === 0 ? (
                <p className="muted">Eşleşen görsel yok.</p>
              ) : (
                shown.map((a) => {
                  return (
                    <article className="asset-tile" key={a.id}>
                      <div className="thumb">
                        {/*
                         * No bytes, no picture — and it says which. `asset.read` is owner/admin, so a
                         * role that may list the library may not always see into it; a broken `<img>`
                         * would read as a missing file rather than as a refused read.
                         */}
                        {a.dataUrlForPrototype ? (
                          <img src={a.dataUrlForPrototype} alt={a.originalFileName || a.id} loading="lazy" />
                        ) : (
                          <span className="small muted" data-nopreview={a.id}>
                            {PREVIEW_REASON_TR[previews[a.id] ?? 'unreadable']}
                          </span>
                        )}
                      </div>
                      <div className="meta">
                        <b>{a.originalFileName || a.id}</b>
                        <span className="small muted">
                          {`${a.width}×${a.height} · ${a.mimeType.replace('image/', '').toUpperCase()} · ${(a.byteSize / 1024).toFixed(0)} KB${alphaNote(a)}`}
                        </span>
                        <span className="small mono" style={{ wordBreak: 'break-all' }}>
                          {`${a.sha256.slice(0, 22)}…`}
                        </span>
                        <AssetUsageLine
                          assetId={a.id}
                          read={usage}
                          onRetry={() => {
                            usage.retry();
                          }}
                        />
                        <div className="row">
                          <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            data-acopy={a.id}
                            onClick={() => {
                              toast('SHA-256 kopyalandı');
                              void copyText(a.sha256).then((ok) => {
                                if (!ok) toast('Panoya yazılamadı — metni elle kopyalayın');
                              });
                            }}
                          >
                            Hash kopyala
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            data-aapply={a.id}
                            onClick={() => setApplyTo(a)}
                          >
                            Şablona uygula…
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <div className="card" style={{ marginBottom: '16px' }}>
          <div className="card-head">
            <h2>
              {"Font asset'leri "}
              <span className="small muted">{`(${fonts.length} yüz)`}</span>
            </h2>
            <label className="btn btn-secondary btn-sm" htmlFor="alFont" style={{ margin: '0' }}>
              Font yükle
            </label>
            <input
              type="file"
              id="alFont"
              accept=".ttf,.otf,.woff,.woff2,font/ttf,font/otf,font/woff,font/woff2"
              multiple
              hidden
              onChange={(e) => {
                setFontLines([]);
                setFontQueue(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Aile</th>
                  <th scope="col">Ağırlık / stil</th>
                  <th scope="col">SHA-256</th>
                  <th scope="col">Boyut</th>
                  <th scope="col">Durum</th>
                </tr>
              </thead>
              <tbody>
                {library.status === 'loading' && library.value === null ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      Okunuyor…
                    </td>
                  </tr>
                ) : library.status === 'error' ? (
                  <tr>
                    <td colSpan={5}>
                      <span className="warn-box" role="alert">
                        {'Font listesi okunamadı. '}
                        <button
                          className="btn btn-secondary btn-sm"
                          type="button"
                          id="alFontRetry"
                          onClick={library.retry}
                        >
                          Yeniden dene
                        </button>
                      </span>
                    </td>
                  </tr>
                ) : fonts.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="muted">
                      {"Font asset'i yok."}
                    </td>
                  </tr>
                ) : (
                  fonts.map((f) => (
                    <tr key={f.id}>
                      <td>{f.family}</td>
                      <td className="tnum">{`${f.weight} · ${f.style}`}</td>
                      <td className="small mono" style={{ wordBreak: 'break-all' }}>
                        {`${f.sha256.slice(0, 24)}…`}
                      </td>
                      <td className="tnum small">{`${(f.byteSize / 1024).toFixed(0)} KB`}</td>
                      <td>
                        {f.state === 'loaded' ? (
                          <span className="pill pill-active">yüklendi</span>
                        ) : f.state === 'failed' ? (
                          <span className="pill pill-revoked">yüklenemedi</span>
                        ) : (
                          <span className="pill pill-neutral">bekliyor</span>
                        )}
                        {f.licenseAcknowledged ? null : (
                          <>
                            {' '}
                            <span className="pill pill-pending">lisans teyidi yok</span>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="card-pad">
            <div className="help">
              {"Font byte'ları ve digest'leri asset gibi saklanır; release "}
              <b>tam font digest'ine</b>
              {' bağlanır. Fallback fontla canonical yayın/çıktı üretilmez — yayın engellenir.'}
            </div>
            <div id="alFontResult">
              {fontLines.map((l) => (
                <div key={l.key}>{l.node}</div>
              ))}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Proje aktarımı</h2>
            <span className="small muted">{`${PROJECT_SCHEMA_NAME} · şema ${PROJECT_SCHEMA_VERSION}`}</span>
          </div>
          <div className="card-pad stack" style={{ gap: '10px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary btn-sm"
                type="button"
                id="alExport"
                /* API GAP: project.export — route-less in the product (PO 7.5); the file is built
                   from the session's own records. */
                onClick={() =>
                  downloadText(
                    `hpw-credentials-proje-${todayIso()}.json`,
                    JSON.stringify(exportProject(db, assets, fonts, PROTOTYPE_VERSION)),
                    'application/json',
                  )
                }
              >
                Projeyi JSON olarak dışa aktar
              </button>
              <label className="btn btn-secondary btn-sm" htmlFor="alImport" style={{ margin: '0' }}>
                Projeyi JSON'dan içe aktar
              </label>
              <input
                type="file"
                id="alImport"
                accept=".json,application/json"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  void readImportFile(file);
                }}
              />
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                id="alClear"
                onClick={() => setConfirmClear(true)}
              >
                Kullanıcı verisini sıfırla
              </button>
            </div>
            <div className="help">
              {'İçe aktarım önce '}
              <b>dry-run</b>
              {
                " çalışır: şema sürümü, boyut/kayıt limitleri, asset imza-MIME-digest tutarlılığı, SVG temizliği, font digest'i, release hash'inin yeniden hesaplanması ve visibility invariant'ları denetlenir. Açık onay verilmeden hiçbir kayıt state'e yazılmaz; yazım sırasında hata olursa tümü geri alınır. Statik prototipte antivirüs taraması yoktur; bu, backend sözleşmesinde server-side defense-in-depth kontrolü olarak tanımlanmıştır."
              }
            </div>
            <div id="alImpResult">
              {checking ? <Spinner label="Dosya denetleniyor…" /> : null}
              {!checking && plan ? <ImportPlanView plan={plan} /> : null}
              {!checking && importResult ? importResult.node : null}
            </div>
          </div>
        </div>
      </div>

      {fontQueue[0] ? (
        <FontMetaModal
          file={fontQueue[0]}
          onCancel={() => void resolveFontMeta(null)}
          onSave={(meta) => void resolveFontMeta(meta)}
        />
      ) : null}

      {applyTo ? (
        <ApplyAssetModal
          asset={applyTo}
          templates={db.templates}
          onCancel={() => setApplyTo(null)}
          onApply={(id, resize) => void applyToTemplate(id, resize)}
        />
      ) : null}

      {confirmImport && plan ? (
        <Modal label="İçe aktarım onayı" wide onClose={() => setConfirmImport(false)}>
          <ModalHead title="İçe aktarım onayı" onClose={() => setConfirmImport(false)} />
          <div className="card-pad">
            <ImportPlanView plan={plan} />
            <p className="help">
              Onaylarsanız kayıtlar tek işlemde yazılır; herhangi bir adım başarısız olursa tamamı geri
              alınır.
            </p>
          </div>
          <div className="card-foot">
            <button
              className="btn btn-ghost"
              data-close="1"
              type="button"
              onClick={() => setConfirmImport(false)}
            >
              Vazgeç
            </button>
            <button className="btn btn-primary" id="alImpGo" type="button" onClick={commitImport}>
              İçe aktar
            </button>
          </div>
        </Modal>
      ) : null}

      {confirmClear ? (
        <Modal label="Sıfırla" onClose={() => setConfirmClear(false)}>
          <ModalHead title="Kullanıcı verisi sıfırlansın mı?" onClose={() => setConfirmClear(false)} />
          <div className="card-pad">
            <p>
              Bu tarayıcıda saklanan yüklenmiş görseller, fontlar ve arayüzde oluşturulan şablon/kayıtlar
              silinir. Demo veriler her açılışta yeniden üretilir.
            </p>
          </div>
          <div className="card-foot">
            <button
              className="btn btn-ghost"
              data-close="1"
              type="button"
              onClick={() => setConfirmClear(false)}
            >
              Vazgeç
            </button>
            <button className="btn btn-dark" id="alClearGo" type="button" onClick={clearUserData}>
              Sıfırla ve yeniden yükle
            </button>
          </div>
        </Modal>
      ) : null}
    </section>
  );
}
